import React, { useState } from 'react';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import { FormikState } from 'formik/dist/types';
import { ApiSanctionEntityType, ApiSanctionType, ApiScreeningDirection, ApiScreeningTest } from '../../api';
import { formatEnumLabel } from '../../common/helper';
import { parseErrorMessage } from '../../common/errors/helpers';
import {
  SalvButton,
  SalvCheckbox,
  SalvDropdown,
  SalvModal,
  SalvNumberField,
  SalvRadioToggleButton,
  SalvRadioToggleGroup,
  SalvSwitch,
  SalvTagsInput,
  SalvTextArea,
  Size,
} from '@salvapp/ui-components';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../../organisation/feature/helper';
import { useMe } from '../../organisation/useMe';
import { SalvButtonVariant } from '@salvapp/ui-components/build/SalvButton/SalvButton.types';
import { useQueryScreeningSelectorsGroups } from '../configure/lists/useQueryScreeningSelectors';
import { DowJonesCustomListCode } from '../configure/ScreeningDataSetup';
import useCustomLists from '../../custom-lists/useCustomLists';
import { DropdownOption } from '@salvapp/ui-components/build/dropdown/dropdown.types';
import { getListGroupLabel } from '../configure/new/helper';
import { DOW_JONES_TYPES, InverseScreeningSource, getInverseScreeningSource } from './inverse';

export interface ScreeningTestModalProps {
  test?: ApiScreeningTest;
  onSubmit?: (values: ScreeningTestModalValues) => Promise<void>;
}

export interface ScreeningTestModalValues extends Omit<
  ApiScreeningTest,
  | 'entityType'
  | 'screenableFields'
  | 'selectorGroupCodes'
  | 'clearanceRules'
  | 'applicationFeatures'
  | 'screeningDirection'
  | 'screeningListType'
  | 'customListCode'
  | 'recordExternalIds'
> {
  entityType: DropdownOption;
  screenableFields?: string;
  selectorGroupCodes?: DropdownOption[];
  clearanceRules?: string;
  applicationFeatures?: string;
  screeningDirection?: ApiScreeningDirection;
  inverseSource?: InverseScreeningSource;
  screeningListType?: DropdownOption;
  customListCode?: DropdownOption;
  recordExternalIds?: string[];
}

export const ScreeningTestModal = ({ test, onSubmit }: ScreeningTestModalProps) => {
  const [showModal, setShowModal] = useState(false);
  const { features } = useMe();
  const inverseEnabled = isFeatureEnabled(ORGANISATION_FEATURE.DAILY_SCREENING_PERSON_SCREENABLE_VALUES, features);

  const djTypeOptions = DOW_JONES_TYPES.map((value) => ({
    key: value,
    value: value,
    label: formatEnumLabel(value),
  }));

  const entityTypes = Object.entries(ApiSanctionEntityType).map(([key, value]) => ({
    key: key,
    value: value,
    label: formatEnumLabel(value),
  }));

  const modalType = !onSubmit ? 'view' : test ? 'duplicate' : 'create';

  const getModalButtonVariant = (): SalvButtonVariant => {
    switch (modalType) {
      case 'create':
        return 'primary';
      case 'duplicate':
        return 'muted';
      case 'view':
        return 'secondary';
    }
  };

  const getModalButtonText = () => {
    switch (modalType) {
      case 'create':
        return 'Add new screening test';
      case 'duplicate':
        return 'Duplicate';
      case 'view':
        return 'View';
    }
  };

  const getModalHeader = () => {
    switch (modalType) {
      case 'create':
        return 'Add new test';
      case 'duplicate':
        return 'Duplicate test';
      case 'view':
        return 'View test';
    }
  };

  const isFormDisabled = modalType === 'view';

  const customLists = useCustomLists();
  const dowJonesListId = customLists.find((list) => list.code === DowJonesCustomListCode.DOW_JONES_SANCTION)?.id;
  const { data: screeningSelectorsGroups } = useQueryScreeningSelectorsGroups(dowJonesListId);
  const groupOptions =
    screeningSelectorsGroups?.map((group) => ({
      key: group.code,
      value: group.code,
      label: getListGroupLabel(group),
    })) ?? [];

  const dowJonesCodes = Object.values(DowJonesCustomListCode) as string[];
  const customListOptions = customLists
    .filter((list) => !!list.code && !dowJonesCodes.includes(list.code))
    .map((list) => {
      const code = list.code!;
      return {
        key: code,
        value: code,
        label: list.name ?? code,
      };
    });

  const initialValues: ScreeningTestModalValues =
    test !== undefined
      ? {
          minId: test.minId,
          maxId: test.maxId,
          entityType: {
            key: test.entityType,
            value: test.entityType,
            label: formatEnumLabel(test.entityType),
          },
          screenableFields: JSON.stringify(test.screenableFields),
          selectorGroupCodes: test.selectorGroupCodes?.map((code) =>
            groupOptions.find((group) => group.value === code)!
          ),
          clearanceRules: JSON.stringify(test.clearanceRules),
          goodlistEnabled: test.goodlistEnabled,
          useExistingHits: test.useExistingHits,
          scoreThreshold: test.scoreThreshold,
          bicLengthThreshold: test.bicLengthThreshold,
          applicationFeatures: JSON.stringify(test.applicationFeatures),
          useOnlyScreenedEntities: test.useOnlyScreenedEntities,
          exemptionRulesEnabled: test.exemptionRulesEnabled,
          screeningDirection: test.screeningDirection ?? ApiScreeningDirection.REGULAR,
          inverseSource: getInverseScreeningSource(test),
          screeningListType:
            test.screeningListType && test.screeningListType !== ApiSanctionType.CUSTOM_SCREENING_LIST
              ? {
                  key: test.screeningListType,
                  value: test.screeningListType,
                  label: formatEnumLabel(test.screeningListType),
                }
              : undefined,
          customListCode: test.customListCode
            ? (customListOptions.find((option) => option.value === test.customListCode) ?? {
                key: test.customListCode,
                value: test.customListCode,
                label: test.customListCode,
              })
            : undefined,
          recordExternalIds: test.recordExternalIds,
        }
      : {
          minId: 0,
          maxId: 0,
          entityType: {
            key: ApiSanctionEntityType.PERSON,
            value: ApiSanctionEntityType.PERSON,
            label: 'Person',
          },
          screeningDirection: ApiScreeningDirection.REGULAR,
        };

  const initialFormikState: Partial<FormikState<ScreeningTestModalValues>> = {
    values: initialValues,
    isSubmitting: false,
  };

  const submit = async (
    values: ScreeningTestModalValues,
    { setStatus, setSubmitting, resetForm }: FormikHelpers<ScreeningTestModalValues>
  ) => {
    try {
      onSubmit && (await onSubmit(values));
      setShowModal(false);
    } catch (e) {
      setStatus(await parseErrorMessage(e));
    }
    resetForm(initialFormikState);
    setSubmitting(false);
  };

  return (
    <Formik onSubmit={submit} initialValues={initialValues} enableReinitialize>
      {({ isSubmitting, values, setFieldValue }: FormikProps<ScreeningTestModalValues>) => {
        const isInverse = values.screeningDirection === ApiScreeningDirection.INVERSE;
        const showRecordIds = values.inverseSource === 'DOW_JONES' || values.inverseSource === 'CUSTOM_LIST';
        return (
          <>
            <SalvButton
              onClick={() => setShowModal(true)}
              variant={getModalButtonVariant()}
              label={getModalButtonText()}
              size={Size.SMALL}
            />
            <SalvModal show={showModal} onHide={() => setShowModal(false)} className={'screening-test-modal'}>
              <Form>
                <SalvModal.Header>{getModalHeader()}</SalvModal.Header>
                <SalvModal.Body>
                  {inverseEnabled && (
                    <SalvRadioToggleGroup
                      value={values.screeningDirection}
                      ariaLabel={'Direction'}
                      size={Size.SMALL}
                      onChange={(value) => {
                        setFieldValue('screeningDirection', value);
                        if (value === ApiScreeningDirection.INVERSE) {
                          const personOption = entityTypes.find((e) => e.value === ApiSanctionEntityType.PERSON)!;
                          setFieldValue('entityType', personOption);

                          if (!values.inverseSource) {
                            setFieldValue('inverseSource', 'DOW_JONES');
                          }
                        }
                      }}
                    >
                      <SalvRadioToggleButton label={'Regular'} value={ApiScreeningDirection.REGULAR} />
                      <SalvRadioToggleButton label={'Inverse'} value={ApiScreeningDirection.INVERSE} />
                    </SalvRadioToggleGroup>
                  )}
                  {isInverse && (
                    <>
                      <SalvRadioToggleGroup
                        value={values.inverseSource}
                        ariaLabel={'Inverse source'}
                        size={Size.SMALL}
                        onChange={(value) => setFieldValue('inverseSource', value)}
                      >
                        <SalvRadioToggleButton label={'Dow Jones'} value={'DOW_JONES'} />
                        <SalvRadioToggleButton label={'Custom List'} value={'CUSTOM_LIST'} />
                        <SalvRadioToggleButton label={'All'} value={'ALL'} />
                      </SalvRadioToggleGroup>
                      {values.inverseSource === 'DOW_JONES' && (
                        <SalvDropdown
                          label='Screening list type'
                          value={values.screeningListType}
                          onChange={([value]) => setFieldValue('screeningListType', value)}
                          options={djTypeOptions}
                          disabled={isFormDisabled}
                          size={Size.SMALL}
                          renderedInBootstrapModal
                        />
                      )}
                      {values.inverseSource === 'CUSTOM_LIST' && (
                        <SalvDropdown
                          label='Custom list'
                          value={values.customListCode}
                          onChange={([value]) => setFieldValue('customListCode', value)}
                          options={customListOptions}
                          disabled={isFormDisabled}
                          size={Size.SMALL}
                          renderedInBootstrapModal
                        />
                      )}
                      {showRecordIds && (
                        <SalvTagsInput
                          label='Record external IDs'
                          description={'All records in scope if empty'}
                          value={values.recordExternalIds ?? []}
                          onChange={(value: string[]) => setFieldValue('recordExternalIds', value)}
                          placeholder='Enter a record id'
                          size={Size.SMALL}
                        />
                      )}
                    </>
                  )}
                  {!isInverse && (
                    <SalvSwitch
                      label='Use existing hits (alert ids range)'
                      selected={values.useExistingHits}
                      onChange={(value) => setFieldValue('useExistingHits', value)}
                      disabled={isFormDisabled}
                    />
                  )}
                  <div className={'field-wrapper'}>
                    <SalvDropdown
                      label='Entity type'
                      value={values.entityType}
                      onChange={([value]) => setFieldValue('entityType', value)}
                      options={entityTypes}
                      disabled={isFormDisabled || isInverse}
                      size={Size.SMALL}
                      renderedInBootstrapModal
                    />
                    {!isInverse && (
                      <>
                        <SalvNumberField
                          label={values.useExistingHits ? 'From alert id' : 'From entity id'}
                          value={values.minId}
                          onChange={(value) => setFieldValue('minId', value)}
                          placeholder='1'
                          disabled={isFormDisabled}
                          size={Size.SMALL}
                        />
                        <SalvNumberField
                          label={values.useExistingHits ? 'To alert id' : 'To entity id'}
                          value={values.maxId}
                          onChange={(value) => setFieldValue('maxId', value)}
                          placeholder='1'
                          disabled={isFormDisabled}
                          size={Size.SMALL}
                        />
                      </>
                    )}
                  </div>
                  {!isInverse && (
                    <SalvTextArea
                      label='Screenable fields'
                      description={'Organisation configuration if empty'}
                      value={values.screenableFields}
                      onChange={(value) => setFieldValue('screenableFields', value)}
                      placeholder='[{"screenableField": "name", "screeningType": "ADVERSE_MEDIA", "screeningSubtype": "NAME"},{"screenableField": "sender_name", "screeningType": "SANCTION", "screeningSubtype": "NAME", "goodlistKey1": "sender_name", "goodlistKey2": "sender_account", "goodlistKey3": "..."}]'
                      disabled={isFormDisabled}
                      size={Size.SMALL}
                      autoSizing
                      minRows={1}
                    />
                  )}
                  <SalvTextArea
                    label='Enabled clearance rule content ids'
                    description={'If empty, all current & drafts will be used'}
                    value={values.clearanceRules}
                    onChange={(value) => setFieldValue('clearanceRules', value)}
                    placeholder='[1,2,3...]'
                    disabled={isFormDisabled}
                    size={Size.SMALL}
                    autoSizing
                    minRows={1}
                  />
                  {!isInverse && (
                    <SalvDropdown
                      label='Enabled List Groups'
                      description={'Overrides Screening Flows List Groups'}
                      options={groupOptions}
                      value={values.selectorGroupCodes}
                      onChange={(value) => setFieldValue('selectorGroupCodes', value)}
                      isMulti
                      disabled={isFormDisabled}
                      size={Size.SMALL}
                      renderedInBootstrapModal
                    />
                  )}
                  <SalvTextArea
                    label='Enabled application features'
                    description={'If not defined active ones will be used'}
                    value={values.applicationFeatures}
                    onChange={(value) => setFieldValue('applicationFeatures', value)}
                    placeholder='["SOME_FEATURE", "SOME_OTHER_FEATURE"...]'
                    disabled={isFormDisabled}
                    size={Size.SMALL}
                    autoSizing
                    minRows={1}
                  />
                  {!isInverse && (
                    <div className={'field-wrapper'}>
                      <SalvNumberField
                        label='Score threshold'
                        description={'Organisation sanction list configuration if empty'}
                        value={values.scoreThreshold}
                        onChange={(value) => setFieldValue('scoreThreshold', value)}
                        placeholder='95'
                        minValue={90}
                        maxValue={100}
                        disabled={isFormDisabled}
                        size={Size.SMALL}
                      />
                      <SalvNumberField
                        label='Bic length threshold'
                        description={'Organisation configuration if empty'}
                        value={values.bicLengthThreshold}
                        onChange={(value) => setFieldValue('bicLengthThreshold', value)}
                        placeholder='8'
                        minValue={1}
                        maxValue={12}
                        disabled={isFormDisabled}
                        size={Size.SMALL}
                      />
                    </div>
                  )}
                  <div>
                    <SalvCheckbox
                      label='Goodlist enabled'
                      onChange={(value) => setFieldValue('goodlistEnabled', value)}
                      selected={values.goodlistEnabled}
                      disabled={isFormDisabled}
                      size={Size.SMALL}
                    />
                    {!isInverse && (
                      <SalvCheckbox
                        label={
                          values?.entityType?.key === ApiSanctionEntityType.PERSON
                            ? 'Use only automatically re-screened entities'
                            : 'Use only screened entities'
                        }
                        onChange={(value) => setFieldValue('useOnlyScreenedEntities', value)}
                        selected={!!values.useOnlyScreenedEntities}
                        disabled={isFormDisabled}
                        size={Size.SMALL}
                      />
                    )}
                  </div>
                  {values?.entityType?.key === ApiSanctionEntityType.TRANSACTION && (
                    <SalvCheckbox
                      label='Transaction exemption rules enabled'
                      onChange={(value) => setFieldValue('exemptionRulesEnabled', value)}
                      selected={!!values.exemptionRulesEnabled}
                      disabled={isFormDisabled}
                      size={Size.SMALL}
                    />
                  )}
                </SalvModal.Body>
                <SalvModal.Footer>
                  <SalvButton label={'Close'} onClick={() => setShowModal(false)} variant='secondary' />
                  {!isFormDisabled && (
                    <SalvButton label={'Save'} disabled={isSubmitting} type='submit' variant='primary' />
                  )}
                </SalvModal.Footer>
              </Form>
            </SalvModal>
          </>
        );
      }}
    </Formik>
  );
};

export default ScreeningTestModal;
